/**
 * MapboxStyleLayer
 *
 * Layer type for Mapbox GL base map styles.
 * These are complete map styles (backgrounds) that replace the entire base map.
 *
 * Unlike overlay layers (WMS, WFS, TMS), Mapbox styles use map.setStyle()
 * which replaces all existing layers. This class handles the style switch
 * and emits events so overlay layers can be re-added.
 *
 * Example style URLs:
 * - mapbox://styles/mapbox/streets-v11
 * - mapbox://styles/mapbox/satellite-v9
 * - mapbox://styles/mapbox/outdoors-v11
 * - mapbox://styles/mapbox/dark-v10
 */

import mapboxgl from 'mapbox-gl';
import { BaseLayer, LayerConfig } from './BaseLayer';
import { LayerRegistry } from './LayerRegistry';
import { logger } from '../../utils/logger';

const log = logger('MapboxStyleLayer');

/**
 * Extended config for Mapbox Style layers
 */
interface MapboxStyleConfig {
  /** The Mapbox style URL (e.g., mapbox://styles/mapbox/streets-v11) */
  styleUrl?: string;
  /** Optional: thumbnail URL for preview in layer list */
  thumbnail?: string;
}

/**
 * MapboxStyleLayer class
 *
 * Manages Mapbox GL base map styles as layers in the Layer Drawer system.
 */
export class MapboxStyleLayer extends BaseLayer {
  /**
   * The style URL from config
   */
  private styleUrl: string;

  /**
   * Previous style URL (for potential rollback)
   */
  private previousStyleUrl: string | null = null;

  /**
   * Constructor
   */
  constructor(map: mapboxgl.Map, config: LayerConfig) {
    super(map, config);

    // Get style URL from config or source_url
    const styleConfig = config.config as MapboxStyleConfig;
    this.styleUrl = styleConfig?.styleUrl || config.source_url || '';

    log.debug(`Constructor: ${config.name}`);

    if (!this.styleUrl) {
      throw new Error('MapboxStyleLayer requires a styleUrl in config or source_url');
    }
  }

  /**
   * Initialize - validate the style URL
   */
  protected async onInitialize(): Promise<void> {
    // Validate style URL format
    if (!this.styleUrl.startsWith('mapbox://styles/') &&
        !this.styleUrl.startsWith('http://') &&
        !this.styleUrl.startsWith('https://')) {
      throw new Error(`Invalid style URL format: ${this.styleUrl}. Expected mapbox://styles/... or http(s)://...`);
    }

    log.debug(`Initialized: ${this.config.name}`);
  }

  /**
   * Enable - switch the map to this style
   *
   * Note: This will trigger a full map style reload.
   * After the style loads, all enabled overlay layers will be re-added to the map.
   *
   * Uses style.load as primary, with idle event as a reliable backup.
   * The idle event fires when the map has finished rendering all tiles,
   * which is more reliable than styledata + isStyleLoaded().
   * See: https://github.com/mapbox/mapbox-gl-js/issues/7579
   */
  protected async onEnable(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // Store current style for potential rollback
        const currentStyle = this.map.getStyle();
        if (currentStyle?.sprite) {
          this.previousStyleUrl = currentStyle.sprite;
        }

        // Check if we're already on this style - no need to switch
        // Compare sprite URLs which contain the style identifier
        const currentSprite = currentStyle?.sprite;
        if (currentSprite && this.styleUrl.includes('mapbox://styles/')) {
          // Extract style ID from both (e.g., "streets-v11")
          const targetId = this.styleUrl.split('/').pop();
          if (targetId && currentSprite.includes(targetId)) {
            log.debug(`Already on style ${this.config.name}, skipping switch`);
            resolve();
            return;
          }
        }

        log.debug(`Switching to: ${this.styleUrl}`);

        // Track if we've already resolved to prevent double resolution
        let hasResolved = false;
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        let backupTimeoutId: ReturnType<typeof setTimeout> | null = null;

        // Cleanup function to remove all listeners
        const cleanup = () => {
          this.map.off('style.load', onStyleLoad);
          this.map.off('idle', onIdle);
          if (timeoutId) clearTimeout(timeoutId);
          if (backupTimeoutId) clearTimeout(backupTimeoutId);
        };

        // Handler that completes the enable process
        const completeEnable = async () => {
          if (hasResolved) return;
          hasResolved = true;
          cleanup();

          // Style loaded successfully

          // Re-add all enabled overlay layers after style change
          // This is necessary because setStyle() destroys all custom layers
          await this.reAddEnabledOverlayLayers();

          resolve();
        };

        // Primary: style.load event (works in most cases)
        const onStyleLoad = () => completeEnable();

        // Backup: idle event fires when map finishes all rendering
        const onIdle = () => {
          if (hasResolved) return;
          completeEnable();
        };

        // Listen for style.load (primary)
        this.map.once('style.load', onStyleLoad);

        // Set up backup: wait 500ms then listen for idle event
        backupTimeoutId = setTimeout(() => {
          if (!hasResolved) {
            this.map.once('idle', onIdle);
          }
        }, 500);

        // Timeout fallback: prevent infinite spinner (10 second max)
        timeoutId = setTimeout(() => {
          if (!hasResolved) {
            log.warn(`Style load timeout for ${this.config.name} - forcing completion`);
            // Force completion even if style may not be fully ready
            // This prevents infinite spinner - layers may need manual refresh
            completeEnable();
          }
        }, 10000);

        // Switch the style
        this.map.setStyle(this.styleUrl);

      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Re-add all enabled overlay layers after a style change
   *
   * When setStyle() is called, all custom layers are destroyed.
   * This method re-enables them by calling their enable() method again.
   */
  private async reAddEnabledOverlayLayers(): Promise<void> {
    try {
      const registry = LayerRegistry.getInstance();
      const enabledLayers = registry.getEnabled();

      // Filter to only overlay layers (not this background layer or other backgrounds)
      const overlayLayers = enabledLayers.filter(layer => {
        const config = layer.getConfig();
        return config.category !== 'backgrounds' && layer.getId() !== this.config.id;
      });

      // Re-add overlay layers after style change

      // Sort overlay layers by their order before re-adding
      // This ensures they're added in the correct z-order
      overlayLayers.sort((a, b) => a.getOrder() - b.getOrder());

      // Re-enable each overlay layer (this will re-add sources and layers to the map)
      for (const layer of overlayLayers) {
        try {
          // First mark as disabled so enable() will actually run
          // We need to call the internal methods to avoid state issues
          await layer.forceReEnable();
        } catch (error) {
          log.warn(`Failed to re-add layer ${layer.getConfig().name}:`, error);
        }
      }

      // Reorder all layers after style change
      registry.reorderAllLayers();
    } catch (error) {
      log.error('Error re-adding overlay layers:', error);
    }
  }

  /**
   * Disable - for background layers, this is a no-op
   *
   * Background layers can't really be "disabled" without switching to another style.
   * The Layer Drawer handles this by enabling a different background layer instead.
   */
  protected async onDisable(): Promise<void> {
    // No-op - when another background is enabled, it calls setStyle
  }

  protected async onSetOpacity(opacity: number): Promise<void> {
    // No-op - base styles don't support opacity
  }

  protected onSetOrder(order: number): void {
    // No-op - base styles are always at the bottom
  }

  protected async onDestroy(): Promise<void> {
    this.previousStyleUrl = null;
  }

  /**
   * Get Mapbox layer IDs - not applicable for base styles
   *
   * Base map styles contain their own layers, we don't create additional ones.
   */
  public getMapboxLayerIds(): string[] {
    return [];
  }

  /**
   * Get Mapbox source IDs - not applicable for base styles
   *
   * Base map styles contain their own sources, we don't create additional ones.
   */
  public getMapboxSourceIds(): string[] {
    return [];
  }

  /**
   * Get the style URL
   */
  public getStyleUrl(): string {
    return this.styleUrl;
  }

  /**
   * Get the previous style URL (if available)
   */
  public getPreviousStyleUrl(): string | null {
    return this.previousStyleUrl;
  }
}
